import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon, ArrowLeftIcon, CreditCardIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import AccountSetupStepper from "../components/AccountSetupStepper";
import VenueDetailsStep from "../components/steps/VenueDetailsStep";
import VenueTermsStep from "../components/steps/VenueTermsStep";
import VenueSubscriptionStep from "../components/steps/VenueSubscriptionStep";
import PaymentStep from "../components/steps/PaymentStep";
import VenueCompletionStep from "../components/steps/VenueCompletionStep";
import config from "../config";
import './CreateAccount.css';

const baseUrl = config.apiBaseUrl;

const CreateVenue = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Venue subscription packages
    const [venueSubscriptions, setVenueSubscriptions] = useState([]);

    useEffect(() => {
        fetch(`${baseUrl}/api/subscription/venue-platform-plans`)
            .then(res => res.json())
            .then(data => setVenueSubscriptions(data))
            .catch(err => console.error('Failed to load subscription plans:', err));
    }, []);

    const [selectedSubscription, setSelectedSubscription] = useState('');

    // Step management
    const [currentStep, setCurrentStep] = useState(0);
    const steps = ['Venue Details', 'Terms & Conditions', 'Subscription', 'Complete Setup'];
    
    // Form data
    const [venueId, setVenueId] = useState(''); // Store venue ID after creation
    const [userId, setUserId] = useState(''); // Store user ID after creation
    const [venueAlreadyExists, setVenueAlreadyExists] = useState(false); // Track if venue already exists
    const [formData, setFormData] = useState({
        shortCodeKey: '',
        venueName: '',
        location: '',
        address: '',
        contactEmail: '',
        contactFirstName: '',
        contactSurname: '',
        password: '',
        confirmPassword: ''
    });
    
    // Validation and state
    const [shortCodeValidationText, setShortCodeValidationText] = useState('');
    const [emailValidationText, setEmailValidationText] = useState('');
    const [passwordValidationText, setPasswordValidationText] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Terms acceptance
    const [venueTermsAccepted, setVenueTermsAccepted] = useState(false);
    
    // Payment state
    const [paymentStatus, setPaymentStatus] = useState(null); // null, 'processing', 'success', 'failed'
    const [paymentError, setPaymentError] = useState('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paymentResult = params.get('step');
        const paymentError = params.get('error');
        
        // Handle payment redirect results
        if (paymentResult === 'complete') {
            setPaymentStatus('success');
            setCurrentStep(3); // Go to completion step
        } else if (paymentResult === 'cancelled') {
            setPaymentStatus('failed');
            setPaymentError(paymentError || 'Payment setup failed');
            setCurrentStep(3); // Go to payment step
        }
    }, [location]);

    // Form validation functions
    function resetValidationText() {
        setShortCodeValidationText('');
        setEmailValidationText('');
        setPasswordValidationText('');
    }

    function validateStep1() {
        resetValidationText();
        let emailPattern = /^[^@]+@[^@]+\.[^@]+$/;
        
        if (!formData.shortCodeKey || formData.shortCodeKey.length === 0) {
            setShortCodeValidationText('Short code is required');
            return false;
        }

        if (formData.shortCodeKey.length > 6) {
            setShortCodeValidationText('Short code must be 6 characters or less');
            return false;
        }

        if (!formData.venueName || formData.venueName.trim().length === 0) {
            setError('Venue name is required');
            return false;
        }

        if (!emailPattern.test(formData.contactEmail)) {
            setEmailValidationText('Please enter a valid email address');
            return false;
        }

        if (!matchValue(formData.password, formData.confirmPassword)) {
            setPasswordValidationText('Passwords do not match');
            return false;
        }

        if(formData.password.length < 8){
            setPasswordValidationText('Password must be at least 8 characters');
            return false;
        }

        return true;
    }

    function validateStep2() {
        return venueTermsAccepted;
    }

    function validateStep3() {
        return selectedSubscription !== '';
    }

    function matchValue(v1, v2) {
        return v1 === v2;
    }

    // Step navigation
    const nextStep = () => {
        if (currentStep === 0 && !validateStep1()) return;
        if (currentStep === 1 && !validateStep2()) {
            setError('Please accept the venue terms and conditions to continue');
            return;
        }
        
        setError('');
        
        // After T&Cs acceptance, create the venue
        if (currentStep === 1) {
            handleCreateVenue();
            return;
        }
        
        if (currentStep === 2 && !validateStep3()) {
            setError('Please select a subscription plan');
            return;
        }
        
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
            
            // If moving to payment step, initiate payment setup
            if (currentStep === 2) {
                handlePaymentSetup();
            }
        } else {
            handleFinalSubmit();
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setError('');
            
            // Reset venue exists flag when going back to allow retry
            if (currentStep === 2) {
                setVenueAlreadyExists(false);
            }
        }
    };

    // Handle venue creation after T&Cs acceptance
    const handleCreateVenue = async () => {
        setError("");
        setLoading(true);

        const venueData = {
            ShortCodeKey: formData.shortCodeKey,
            VenueName: formData.venueName,
            Location: formData.location,
            Address: formData.address,
            ContactEmail: formData.contactEmail,
            ContactFirstName: formData.contactFirstName,
            ContactSurname: formData.contactSurname,
            Password: formData.password,
            VenueTermsAccepted: venueTermsAccepted
        };

        try {
            const response = await fetch(`${baseUrl}/api/users/venue-signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(venueData)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Venue created successfully:', data);
                
                // Store the venue ID and user ID for payment setup
                setVenueId(data.venueId || data.id);
                setUserId(data.userId);
                setVenueAlreadyExists(false);
                
                // Move to subscription step
                setCurrentStep(currentStep + 1);
            } else {
                const errorData = await response.json();
                
                // Check if venue already exists
                if (errorData.code === "venue_already_exists") {
                    console.log('Venue already exists, continuing...');
                    setVenueAlreadyExists(true);
                    
                    // Extract venue ID from error response if available
                    if (errorData.venueId) {
                        setVenueId(errorData.venueId);
                    }
                    
                    // Move to subscription step
                    setCurrentStep(currentStep + 1);
                } else {
                    throw new Error(errorData.message || 'Failed to create venue');
                }
            }
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to create venue. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle payment setup (redirect to GoCardless)
    const handlePaymentSetup = async () => {
        setIsProcessingPayment(true);
        setPaymentStatus('processing');

        try {
            // Call your API to get GoCardless redirect URL with the venue ID
            const response = await fetch(`${baseUrl}/api/paymentadmin/setupvenuepayment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    VenueId: venueId,
                    SubscriptionId: selectedSubscription,
                    UserId: userId
                })
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Payment setup response:', data);
                
                // Check for different possible redirect URL properties
                const redirectUrl = data.mandateUrl || data.redirectUrl || data.redirect_url || data.billingRequestFlowUrl || data.url;
                
                if (redirectUrl) {
                    // Redirect to GoCardless payment flow
                    console.log('Redirecting to:', redirectUrl);
                    window.location.href = redirectUrl;
                } else {
                    console.error('No redirect URL found in response:', data);
                    throw new Error('No payment redirect URL received from server');
                }
            } else {
                const errorData = await response.json();
                console.error('Payment setup failed:', errorData);
                throw new Error(errorData.message || 'Failed to setup payment');
            }
        } catch (error) {
            console.error('Payment setup error:', error);
            setPaymentStatus('failed');
            setPaymentError(error.message || 'Failed to setup payment. Please try again.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    // Handle final venue creation (after successful payment)
    const handleFinalSubmit = async () => {
        if (paymentStatus !== 'success') {
            setError('Payment setup must be completed before finishing venue creation');
            return;
        }

        setError("");
        setLoading(true);

        try {
            // Complete venue creation - navigate to activation instructions
            navigate('/venue-activation');
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to complete venue creation. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Retry payment setup
    const handleRetryPayment = () => {
        setPaymentStatus(null);
        setPaymentError('');
        handlePaymentSetup();
    };

    return (
        <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl w-full space-y-8">
                <div className="text-center">
                    <BuildingOfficeIcon className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                    <h2 className="text-3xl font-bold text-white mb-2">Create Your Venue</h2>
                    <p className="text-gray-400">Join the World's High Street as a venue partner</p>
                </div>

                <AccountSetupStepper currentStep={currentStep} />
                
                {error && (
                    <div className="bg-gray-900 border border-gray-700 text-red-300 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                <div className="card p-8">
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-white mb-2">
                            Step {currentStep + 1} of {steps.length}: {steps[currentStep]}
                        </h3>
                        <div className="w-full bg-gray-800 rounded-full h-2">
                            <div 
                                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Render current step */}
                    {currentStep === 0 && (
                        <VenueDetailsStep
                            formData={formData}
                            setFormData={setFormData}
                            shortCodeValidationText={shortCodeValidationText}
                            emailValidationText={emailValidationText}
                            passwordValidationText={passwordValidationText}
                        />
                    )}
                    {currentStep === 1 && (
                        <VenueTermsStep
                            venueTermsAccepted={venueTermsAccepted}
                            setVenueTermsAccepted={setVenueTermsAccepted}
                        />
                    )}
                    {currentStep === 2 && (
                        <VenueSubscriptionStep
                            subscriptions={venueSubscriptions}
                            selectedSubscription={selectedSubscription}
                            setSelectedSubscription={setSelectedSubscription}
                        />
                    )}
                    {currentStep === 3 && paymentStatus !== 'success' && (
                        <PaymentStep
                            paymentStatus={paymentStatus}
                            paymentError={paymentError}
                            isProcessing={isProcessingPayment}
                            onRetry={handleRetryPayment}
                        />
                    )}
                    {currentStep === 3 && paymentStatus === 'success' && (
                        <VenueCompletionStep
                            venueAlreadyExists={venueAlreadyExists}
                            venueName={formData.venueName}
                            shortCodeKey={formData.shortCodeKey}
                            onNavigateToActivation={() => navigate('/venue-activation')}
                        />
                    )}

                    {/* Navigation buttons */}
                    <div className="flex justify-between mt-8">
                        <button
                            type="button"
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                                currentStep === 0
                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-gray-600 text-white hover:bg-gray-500'
                            }`}
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Previous
                        </button>

                        <button
                            type="button"
                            onClick={nextStep}
                            disabled={loading || isProcessingPayment || (currentStep === 3 && paymentStatus !== 'success')}
                            className="flex items-center btn-primary"
                        >
                            {loading || isProcessingPayment ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {currentStep === 1 ? (venueAlreadyExists ? 'Continuing...' : 'Creating Venue...') : currentStep === 3 ? 'Processing...' : 'Loading...'}
                                </>
                            ) : currentStep === 1 ? (
                                <>
                                    <CheckIcon className="h-4 w-4 mr-2" />
                                    {venueAlreadyExists ? 'Continue' : 'Create Venue'}
                                </>
                            ) : currentStep === steps.length - 1 ? (
                                paymentStatus === 'success' ? (
                                    <>
                                        <CheckIcon className="h-4 w-4 mr-2" />
                                        Complete Setup
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className="h-4 w-4 mr-2" />
                                        Finish
                                    </>
                                )
                            ) : currentStep === 2 ? (
                                <>
                                    <CreditCardIcon className="h-4 w-4 mr-2" />
                                    Setup Payment
                                </>
                            ) : (
                                <>
                                    Next
                                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateVenue;
